/** Human-readable names for batch result files (zip paths + save-as). */

export function urlBasename(url: string): string {
  try {
    const path = decodeURIComponent((url || '').split('?')[0]);
    return path.split('/').filter(Boolean).pop() || '';
  } catch {
    return '';
  }
}

type BatchKind = 'summary' | 'clean' | 'jsonl' | 'raw' | 'errors' | 'input';

function classifyBatchFile(basename: string): BatchKind | null {
  const n = (basename || '').toLowerCase();
  if (n === 'run_summary.json') return 'summary';
  if (n === 'records.json') return 'clean';
  if (n === 'records.jsonl') return 'jsonl';
  if (n === 'input.jsonl') return 'input';
  if (n === 'errors.jsonl' || /^errors.*\.jsonl$/.test(n)) return 'errors';
  if (n === 'predictions.jsonl' || /^predictions.*\.jsonl$/.test(n)) return 'raw';
  return null;
}

function basenameOf(basenameOrUrl: string): string {
  if (!basenameOrUrl) return '';
  if (basenameOrUrl.includes('://') || basenameOrUrl.startsWith('/')) {
    return urlBasename(basenameOrUrl);
  }
  return basenameOrUrl.split('/').pop() || basenameOrUrl;
}

/** Zip member path. Clean results sit at the zip root; debug files go under debug/. */
export function friendlyDownloadPath(basenameOrUrl: string): string | null {
  switch (classifyBatchFile(basenameOf(basenameOrUrl))) {
    case 'summary':
      return '00_RUN_SUMMARY.json';
    case 'clean':
      return '01_CLEAN_RESULTS.json';
    case 'jsonl':
      return 'debug/02_results_line_by_line.jsonl';
    case 'raw':
      return 'debug/03_raw_batch_output.jsonl';
    case 'errors':
      return 'debug/04_batch_errors.jsonl';
    case 'input':
      return 'debug/00_original_input.jsonl';
    default:
      return null;
  }
}

/** Filename only (no folders) for <a download> / blob save-as. */
export function friendlySaveAsName(basenameOrUrl: string): string {
  const path = friendlyDownloadPath(basenameOrUrl);
  if (path) return path.split('/').pop() || path;
  return basenameOf(basenameOrUrl) || 'download';
}

export function friendlyDownloadLabel(basenameOrUrl: string): string | null {
  switch (classifyBatchFile(basenameOf(basenameOrUrl))) {
    case 'summary':
      return 'Run summary';
    case 'clean':
      return 'Clean results (JSON)';
    case 'jsonl':
      return 'Results (JSONL)';
    case 'raw':
      return 'Raw batch output (debug)';
    case 'errors':
      return 'Batch errors (debug)';
    case 'input':
      return 'Original input';
    default:
      return null;
  }
}
